/* ════════════ THE LOCK-ON ════════════
   The state page's atmosphere: targeting brackets acquire random
   points, hold them in the crosshair for a breath, then lose
   interest and move on. It was never personal. That's the problem. */

import { onPage } from "./router";

const REDUCED = matchMedia("(prefers-reduced-motion: reduce)").matches;

function lockon(signal: AbortSignal) {
  const c = document.querySelector<HTMLCanvasElement>("#lockon")!;
  const ctx = c.getContext("2d")!;

  const MAGENTA = "#ff2e88";
  const GREY = "#9aa0a6";

  type Mark = {
    x: number;
    y: number;
    t: number; // age in frames
    life: number;
    r: number; // bracket box radius
    col: string;
  };
  let marks: Mark[] = [];

  const spawn = (): Mark => {
    const isHot = Math.random() < 0.18;
    return {
      x: Math.random() * c.width,
      y: Math.random() * c.height,
      t: 0,
      life: 180 + Math.random() * 240,
      r: isHot ? 16 + Math.random() * 14 : 9 + Math.random() * 8,
      col: isHot ? MAGENTA : GREY,
    };
  };

  const seed = () => {
    c.width = innerWidth;
    c.height = innerHeight;
    const count = Math.min(26, Math.floor((c.width * c.height) / 70000));
    marks = Array.from({ length: count }, () => {
      const m = spawn();
      m.t = Math.random() * m.life; // desync
      return m;
    });
  };
  seed();
  addEventListener("resize", seed, { signal });

  const corner = (x: number, y: number, dx: number, dy: number, len: number) => {
    ctx.beginPath();
    ctx.moveTo(x + dx * len, y);
    ctx.lineTo(x, y);
    ctx.lineTo(x, y + dy * len);
    ctx.stroke();
  };

  const draw = () => {
    ctx.clearRect(0, 0, c.width, c.height);
    for (const m of marks) {
      const p = m.t / m.life;
      const a = p < 0.15 ? p / 0.15 : p > 0.7 ? (1 - p) / 0.3 : 1;
      // acquisition: brackets close in from 1.8x to 1x over the first 15%
      const r = m.r * (p < 0.15 ? 1.8 - 0.8 * (p / 0.15) : 1);
      const hot = m.col === MAGENTA;
      ctx.globalAlpha = a * (hot ? 0.45 : 0.18);
      ctx.strokeStyle = m.col;
      ctx.lineWidth = 1;
      const len = r * 0.45;
      corner(m.x - r, m.y - r, 1, 1, len);
      corner(m.x + r, m.y - r, -1, 1, len);
      corner(m.x - r, m.y + r, 1, -1, len);
      corner(m.x + r, m.y + r, -1, -1, len);
      // once locked, the center cross appears
      if (p > 0.2) {
        ctx.beginPath();
        ctx.moveTo(m.x - 3, m.y);
        ctx.lineTo(m.x + 3, m.y);
        ctx.moveTo(m.x, m.y - 3);
        ctx.lineTo(m.x, m.y + 3);
        ctx.stroke();
      }
    }
    ctx.globalAlpha = 1;
  };

  if (REDUCED) {
    draw();
    return;
  }
  const tick = () => {
    if (signal.aborted) return;
    for (let i = 0; i < marks.length; i++) {
      const m = marks[i]!;
      m.t++;
      if (m.t >= m.life) marks[i] = spawn();
    }
    draw();
    requestAnimationFrame(tick);
  };
  tick();
}

onPage(lockon);
